import { useRanger } from '@tanstack/react-ranger';
import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Slider Component - Built with TanStack Ranger
 *
 * A flexible, headless slider component that supports:
 * - Single and multi-value sliders
 * - Custom styling with gradients (perfect for color pickers)
 * - HSL sliders for hue, saturation, and lightness
 * - Rainbow/color spectrum sliders
 *
 * @example
 * // Normal slider
 * <Slider value={[50]} onChange={setValue} min={0} max={100} />
 *
 * @example
 * // HSL Hue slider (0-360)
 * <Slider
 *   value={[hue]}
 *   onChange={(v) => setHue(v[0])}
 *   min={0}
 *   max={360}
 *   trackStyle={{
 *     background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
 *   }}
 * />
 *
 * @example
 * // HSL Saturation slider (0-100)
 * <Slider
 *   value={[saturation]}
 *   onChange={(v) => setSaturation(v[0])}
 *   min={0}
 *   max={100}
 *   trackStyle={{
 *     background: `linear-gradient(to right, hsl(${hue}, 0%, 50%), hsl(${hue}, 100%, 50%))`
 *   }}
 * />
 *
 * @example
 * // Range slider (multi-value)
 * <Slider value={[20, 80]} onChange={setRange} min={0} max={100} />
 *
 * @example
 * // Slider with custom gradient background (disable active segment to prevent blocking)
 * <Slider
 *   value={[hue]}
 *   onChange={(v) => setHue(v[0])}
 *   min={0}
 *   max={360}
 *   showActiveSegment={false}
 *   trackStyle={{
 *     background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
 *   }}
 * />
 */

// Define the component's props
export type SliderProps = {
    /**
     * The value(s) of the slider. An array with one number is a single-thumb slider.
     * An array with two or more numbers creates a multi-thumb slider.
     */
    value: number[];
    /** Callback function when the value changes. */
    onChange: (newValues: number[]) => void;
    /** The minimum value of the slider. @default 0 */
    min?: number;
    /** The maximum value of the slider. @default 100 */
    max?: number;
    /** The step size for each movement. @default 1 */
    stepSize?: number;
    /** Optional class name to apply to the root element. */
    className?: string;
    /** Whether the slider is disabled. */
    disabled?: boolean;
    /** Optional background style for the track (e.g., gradient for color sliders) */
    trackStyle?: React.CSSProperties;
    /** Optional custom class for the track */
    trackClassName?: string;
    /** Whether to show the active range segment overlay. Set to false to prevent blocking custom backgrounds. @default true */
    showActiveSegment?: boolean;
};

function Slider({ ref, ...props }: SliderProps & { ref?: React.RefObject<HTMLDivElement | null> }) {
    const {
        className,
        value,
        onChange,
        min = 0,
        max = 100,
        stepSize = 1,
        disabled = false,
        trackStyle,
        trackClassName,
        showActiveSegment = true,
    } = props;

    const rangerRef = React.useRef<HTMLDivElement>(null);

    // TanStack Ranger provides the headless logic
    const rangerInstance = useRanger({
        getRangerElement: () => rangerRef.current,
        values: value,
        min,
        max,
        stepSize,
        onChange: instance => onChange([...instance.sortedValues]),
    });

    return (
        <div
            ref={ref}
            className={cn(
                'relative flex w-full touch-none select-none items-center',
                disabled && 'opacity-50 pointer-events-none',
                className,
            )}
        >
            {/* The main track of the slider */}
            <div
                ref={rangerRef}
                className={cn(
                    'relative h-2 w-full grow overflow-hidden rounded-full bg-secondary',
                    trackClassName,
                )}
                style={trackStyle}
            >
                {/* Active range segments */}
                {showActiveSegment && value.length > 0 && (
                    <div
                        className="absolute h-full bg-primary"
                        style={{
                            left: value.length === 1
                                ? '0%'
                                : `${(Math.min(...value) - min) / (max - min) * 100}%`,
                            width: value.length === 1
                                ? `${(value[0] - min) / (max - min) * 100}%`
                                : `${(Math.max(...value) - Math.min(...value)) / (max - min) * 100}%`,
                        }}
                    />
                )}
            </div>

            {/* Draggable handles/thumbs */}
            {rangerInstance.handles().map((handle, index) => {
                const handleValue = handle.value;
                const percentage = ((handleValue - min) / (max - min)) * 100;
                const key = `handle-${handleValue}-${index}`;

                return (
                    <button
                        key={key}
                        type="button"
                        onKeyDown={handle.onKeyDownHandler}
                        onMouseDown={handle.onMouseDownHandler}
                        onTouchStart={handle.onTouchStart}
                        style={{
                            position: 'absolute',
                            left: `${percentage}%`,
                            transform: 'translateX(-50%)',
                        }}
                        className={cn(
                            'block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
                            handle.isActive && 'ring-2 ring-ring ring-offset-2',
                        )}
                        disabled={disabled}
                        aria-label={`Slider thumb ${index + 1}`}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={handleValue}
                        aria-valuetext={String(handleValue)}
                    />
                );
            })}
        </div>
    );
}

Slider.displayName = 'Slider';

export { Slider };
